/**
 * Repositories.
 *
 * React components never issue IndexedDB calls. They talk to these interfaces,
 * which is what makes the storage swappable — a memory implementation for tests
 * and private browsing today, and the same surface with a sync layer behind it
 * later.
 *
 * Where storage is unavailable the app degrades to a working session rather than
 * failing: the memory implementation satisfies the same interface, so nothing
 * upstream needs a special case.
 */

import { STORES, isIndexedDbAvailable, openDb, request, transact, type StoreName } from './db';
import {
  bumped,
  freshMeta,
  type FeatureStateRecord,
  type PracticeSessionRecord,
  type SettingsRecord,
  type StoredDocument,
  type SyncMeta,
} from './types';

export interface DocumentRepository {
  list(options?: { includeDeleted?: boolean }): Promise<StoredDocument[]>;
  get(id: string): Promise<StoredDocument | null>;
  put(document: StoredDocument): Promise<StoredDocument>;
  /** Soft delete: writes a tombstone so the removal can travel. */
  remove(id: string): Promise<void>;
  /** Undo a soft delete. The record was never destroyed, so this is exact. */
  restore(id: string): Promise<StoredDocument | null>;
}

export interface KeyedRepository<T extends SyncMeta & { key: string }> {
  all(): Promise<T[]>;
  get(key: string): Promise<T | null>;
  set(key: string, value: Record<string, unknown>): Promise<T>;
}

export interface SessionRepository {
  list(limit?: number): Promise<PracticeSessionRecord[]>;
  append(
    session: Omit<PracticeSessionRecord, keyof SyncMeta> & Partial<SyncMeta>,
  ): Promise<PracticeSessionRecord>;
}

export interface Repositories {
  documents: DocumentRepository;
  featureState: KeyedRepository<FeatureStateRecord>;
  settings: KeyedRepository<SettingsRecord>;
  sessions: SessionRepository;
  /** True when writes are only in memory, so the UI can tell the truth about it. */
  readonly ephemeral: boolean;
}

// ------------------------------------------------------------------ IndexedDB

class IdbDocuments implements DocumentRepository {
  async list(options: { includeDeleted?: boolean } = {}): Promise<StoredDocument[]> {
    const db = await openDb();
    const rows = await transact(db, STORES.documents, 'readonly', (tx) =>
      request(tx.objectStore(STORES.documents).getAll() as IDBRequest<StoredDocument[]>),
    );
    const live = options.includeDeleted ? rows : rows.filter((row) => row.deletedAt === null);
    return live.sort((a, b) => b.updatedAt - a.updatedAt);
  }

  async get(id: string): Promise<StoredDocument | null> {
    const db = await openDb();
    const row = await transact(db, STORES.documents, 'readonly', (tx) =>
      request(tx.objectStore(STORES.documents).get(id) as IDBRequest<StoredDocument | undefined>),
    );
    return row ?? null;
  }

  async put(document: StoredDocument): Promise<StoredDocument> {
    const db = await openDb();
    await transact(db, STORES.documents, 'readwrite', (tx) => {
      tx.objectStore(STORES.documents).put(document);
    });
    return document;
  }

  async remove(id: string): Promise<void> {
    const existing = await this.get(id);
    if (!existing) return;
    await this.put({ ...bumped(existing), deletedAt: Date.now() });
  }

  async restore(id: string): Promise<StoredDocument | null> {
    const existing = await this.get(id);
    if (!existing) return null;
    return this.put({ ...bumped(existing), deletedAt: null });
  }
}

class IdbKeyed<T extends SyncMeta & { key: string; value: Record<string, unknown> }>
  implements KeyedRepository<T>
{
  constructor(private store: StoreName) {}

  async all(): Promise<T[]> {
    const db = await openDb();
    return transact(db, this.store, 'readonly', (tx) =>
      request(tx.objectStore(this.store).getAll() as IDBRequest<T[]>),
    );
  }

  async get(key: string): Promise<T | null> {
    const db = await openDb();
    const row = await transact(db, this.store, 'readonly', (tx) =>
      request(tx.objectStore(this.store).get(key) as IDBRequest<T | undefined>),
    );
    return row ?? null;
  }

  async set(key: string, value: Record<string, unknown>): Promise<T> {
    const existing = await this.get(key);
    const record = existing
      ? ({ ...bumped(existing), value } as T)
      : ({ ...freshMeta(), key, value } as unknown as T);
    const db = await openDb();
    await transact(db, this.store, 'readwrite', (tx) => {
      tx.objectStore(this.store).put(record);
    });
    return record;
  }
}

class IdbSessions implements SessionRepository {
  async list(limit = 100): Promise<PracticeSessionRecord[]> {
    const db = await openDb();
    const rows = await transact(db, STORES.practiceSessions, 'readonly', (tx) =>
      request(
        tx.objectStore(STORES.practiceSessions).getAll() as IDBRequest<PracticeSessionRecord[]>,
      ),
    );
    return rows.sort((a, b) => b.startedAt - a.startedAt).slice(0, limit);
  }

  async append(
    session: Omit<PracticeSessionRecord, keyof SyncMeta> & Partial<SyncMeta>,
  ): Promise<PracticeSessionRecord> {
    const record: PracticeSessionRecord = { ...freshMeta(), ...session };
    const db = await openDb();
    await transact(db, STORES.practiceSessions, 'readwrite', (tx) => {
      tx.objectStore(STORES.practiceSessions).put(record);
    });
    return record;
  }
}

// --------------------------------------------------------------------- memory

class MemoryDocuments implements DocumentRepository {
  private rows = new Map<string, StoredDocument>();

  async list(options: { includeDeleted?: boolean } = {}) {
    const all = [...this.rows.values()];
    const live = options.includeDeleted ? all : all.filter((r) => r.deletedAt === null);
    return live.sort((a, b) => b.updatedAt - a.updatedAt);
  }
  async get(id: string) {
    return this.rows.get(id) ?? null;
  }
  async put(document: StoredDocument) {
    this.rows.set(document.id, document);
    return document;
  }
  async remove(id: string) {
    const existing = this.rows.get(id);
    if (existing) this.rows.set(id, { ...bumped(existing), deletedAt: Date.now() });
  }
  async restore(id: string) {
    const existing = this.rows.get(id);
    if (!existing) return null;
    const restored = { ...bumped(existing), deletedAt: null };
    this.rows.set(id, restored);
    return restored;
  }
}

class MemoryKeyed<T extends SyncMeta & { key: string; value: Record<string, unknown> }>
  implements KeyedRepository<T>
{
  private rows = new Map<string, T>();

  async all() {
    return [...this.rows.values()];
  }
  async get(key: string) {
    return this.rows.get(key) ?? null;
  }
  async set(key: string, value: Record<string, unknown>) {
    const existing = this.rows.get(key);
    const record = existing
      ? ({ ...bumped(existing), value } as T)
      : ({ ...freshMeta(), key, value } as unknown as T);
    this.rows.set(key, record);
    return record;
  }
}

class MemorySessions implements SessionRepository {
  private rows: PracticeSessionRecord[] = [];

  async list(limit = 100) {
    return [...this.rows].sort((a, b) => b.startedAt - a.startedAt).slice(0, limit);
  }
  async append(session: Omit<PracticeSessionRecord, keyof SyncMeta> & Partial<SyncMeta>) {
    const record: PracticeSessionRecord = { ...freshMeta(), ...session };
    this.rows.push(record);
    return record;
  }
}

// --------------------------------------------------------------------- factory

export function createMemoryRepositories(): Repositories {
  return {
    documents: new MemoryDocuments(),
    featureState: new MemoryKeyed<FeatureStateRecord>(),
    settings: new MemoryKeyed<SettingsRecord>(),
    sessions: new MemorySessions(),
    ephemeral: true,
  };
}

/**
 * Storage that announces nothing.
 *
 * What the sync engine reads and writes through. A pull applying the server's
 * own records must not look like a learner edit — see `writeLog.ts`.
 */
export function createRawRepositories(): Repositories {
  if (!isIndexedDbAvailable()) return createMemoryRepositories();
  return {
    documents: new IdbDocuments(),
    featureState: new IdbKeyed<FeatureStateRecord>(STORES.featureState),
    settings: new IdbKeyed<SettingsRecord>(STORES.settings),
    sessions: new IdbSessions(),
    ephemeral: false,
  };
}

/** Storage that announces learner writes, for feature screens. */
export function createRepositories(): Repositories {
  return withWriteNotifications(createRawRepositories());
}

// ------------------------------------------------------- write notifications

/**
 * Write announcements live in `writeLog.ts`, not here.
 *
 * Lesson progress is written through `curriculum/progress.ts` rather than
 * through a repository, and it has to reach the same listener — a progress save
 * that never reaches the outbox is progress that stays on one iPad forever.
 * Putting the channel in its own module lets both announce without importing
 * each other.
 */
export {
  announceWrite,
  observeWrites,
  resetWriteLog,
  type RecordWrite,
  type WriteKind,
} from './writeLog';

import { announceWrite as announce } from './writeLog';

/**
 * Wrap repositories so successful writes are announced.
 *
 * A decorator rather than a change inside each class: the storage classes stay
 * about storage, and there is exactly one place where "what counts as a
 * syncable write" is decided.
 */
export function withWriteNotifications(inner: Repositories): Repositories {
  return {
    ephemeral: inner.ephemeral,
    featureState: inner.featureState,
    documents: {
      list: (options) => inner.documents.list(options),
      get: (id) => inner.documents.get(id),
      async put(document) {
        const saved = await inner.documents.put(document);
        announce({ kind: 'document', id: saved.id, deleted: saved.deletedAt !== null });
        return saved;
      },
      async remove(id) {
        await inner.documents.remove(id);
        announce({ kind: 'document', id, deleted: true });
      },
      async restore(id) {
        const restored = await inner.documents.restore(id);
        if (restored) announce({ kind: 'document', id });
        return restored;
      },
    },
    settings: {
      all: () => inner.settings.all(),
      get: (key) => inner.settings.get(key),
      async set(key, value) {
        const saved = await inner.settings.set(key, value);
        announce({ kind: 'user_settings', id: key });
        return saved;
      },
    },
    sessions: {
      list: (limit) => inner.sessions.list(limit),
      async append(session) {
        const saved = await inner.sessions.append(session);
        announce({ kind: 'practice_session', id: saved.id });
        return saved;
      },
    },
  };
}

let shared: Repositories | null = null;

/** The app's repositories. One instance so every screen sees the same data. */
export function repositories(): Repositories {
  if (!shared) shared = createRepositories();
  return shared;
}

/** Test seam. */
export function setRepositories(next: Repositories | null): void {
  shared = next;
}
